import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { authGet, authPut } from '../../services/apiService';
import { logout } from '../../services/authService';

/**
 * User profile page: display user info, manage ADO/GitHub PATs, logout.
 */
const UserProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adoPat, setAdoPat] = useState('');
  const [githubPat, setGithubPat] = useState('');
  const [adoDirty, setAdoDirty] = useState(false);
  const [githubDirty, setGithubDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await authGet('/api/auth/me');
        if (response && response.success && response.user) {
          setProfile(response.user);
        } else {
          setError('Failed to load profile');
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSavePats = async () => {
    const body = {};
    if (adoDirty) body.adoPat = adoPat;
    if (githubDirty) body.githubPat = githubPat;
    if (Object.keys(body).length === 0) {
      setSaveError('Change a field and click Save to update.');
      return;
    }
    try {
      setSaving(true);
      setSaveSuccess(false);
      setSaveError(null);
      const response = await authPut('/api/auth/profile/pats', body);
      if (response && response.success) {
        setProfile(prev => prev ? { ...prev, hasAdoPat: response.hasAdoPat, hasGithubPat: response.hasGithubPat } : null);
        setAdoPat('');
        setGithubPat('');
        setAdoDirty(false);
        setGithubDirty(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 5000);
      } else {
        setSaveError(response?.message || 'Failed to save PATs');
      }
    } catch (err) {
      console.error('Error saving PATs:', err);
      setSaveError(err.message || 'Failed to save PATs');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error || 'Profile not found'}
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth={640} mx="auto">
      <Typography variant="h5" gutterBottom fontWeight={600}>
        User Profile
      </Typography>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary">Username</Typography>
          <Typography variant="body1" gutterBottom>{profile.username}</Typography>
          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
          <Typography variant="body1" gutterBottom>{profile.email}</Typography>
          <Typography variant="subtitle2" color="text.secondary">Display name</Typography>
          <Typography variant="body1" gutterBottom>{profile.displayName}</Typography>
          <Typography variant="subtitle2" color="text.secondary">Role</Typography>
          <Typography variant="body1">{profile.role}</Typography>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom fontWeight={600}>
        Personal Access Tokens
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add your ADO and GitHub PATs here. They will be used when you run ADO or GitHub update actions. Only you or an admin can change these.
      </Typography>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box mb={2}>
            <TextField
              fullWidth
              type="password"
              label="ADO Personal Access Token"
              placeholder={profile.hasAdoPat ? 'Enter new value to change, or leave blank to keep current' : 'Enter your ADO PAT'}
              value={adoPat}
              onChange={(e) => { setAdoPat(e.target.value); setAdoDirty(true); }}
              variant="outlined"
              size="small"
              helperText={profile.hasAdoPat ? 'Saved' : 'Not set'}
            />
          </Box>
          <Box mb={2}>
            <TextField
              fullWidth
              type="password"
              label="GitHub Personal Access Token"
              placeholder={profile.hasGithubPat ? 'Enter new value to change, or leave blank to keep current' : 'Enter your GitHub PAT'}
              value={githubPat}
              onChange={(e) => { setGithubPat(e.target.value); setGithubDirty(true); }}
              variant="outlined"
              size="small"
              helperText={profile.hasGithubPat ? 'Saved' : 'Not set'}
            />
          </Box>
          {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>PATs updated successfully.</Alert>}
          {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>}
          <Button variant="contained" onClick={handleSavePats} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Save PATs'}
          </Button>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="body2" color="text.secondary">
          Sign out of your account. You will need to log in again to access protected pages.
        </Typography>
        <Button variant="outlined" color="primary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
    </Box>
  );
};

export default UserProfile;
